import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { articles, categories, tags } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.id === id) || fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = (article.relatedIds || [])
    .map((rid: string) => articles.find((a) => a.id === rid))
    .filter(Boolean) as typeof articles;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜黑料网',
    },
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://moonr.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `http://moonr.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} | 51吃瓜黑料网`}
        description={article.excerpt}
        keywords={`51吃瓜黑料网, ${article.tags.join(', ')}, 吃瓜, 黑料`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 py-6 md:py-8 bg-background-50">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            {category && (
              <>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {article.category}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
              </>
            )}
            <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <article className="lg:w-[65%]">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-bold rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="px-2 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded-full whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(article.viewCount / 10000).toFixed(1)}万 阅读
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount} 评论
                </span>
              </div>
            </div>

            <div className="w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden mb-8 bg-background-100">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full border border-background-200/70 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 p-5 rounded-lg bg-background-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500 mt-0.5">{article.authorBio}</p>
              </div>
            </div>
          </article>

          <aside className="lg:w-[35%]">
            <div className="sticky top-24">
              <div className="p-5 rounded-lg bg-background-100 mb-6">
                <h3 className="text-sm font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-accent-500"></i>
                  相关推荐
                </h3>
                {relatedArticles.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 flex-shrink-0">
                          <img
                            src={rel.imageUrl}
                            alt={rel.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                            {rel.title}
                          </p>
                          <p className="mt-1 text-xs text-foreground-400">{rel.publishDate}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400">暂无相关推荐</p>
                )}
              </div>

              <div className="p-5 rounded-lg bg-accent-50">
                <h3 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-accent-500"></i>
                  热门标签
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 8).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-background-50 text-foreground-500 text-xs rounded-full border border-background-200/70 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-10 bg-background-50 border-t border-background-200/50">
          <div className="text-center">
            <h3 className="font-heading text-lg md:text-xl text-foreground-950">还想看更多？</h3>
            <p className="mt-2 text-sm text-foreground-500">继续浏览精彩内容，不错过任何猛料</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              {categories.slice(0, 4).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium hover:bg-primary-50 hover:text-primary-500 transition-all duration-200 whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}